/**
 * nav-search.js — the header search. Markup: snippets/nav-search.liquid.
 * Result rows: sections/nav-search-results.liquid. Animations: assets/nav-search.css.
 *
 * <nav-search> holds the icon, the bar and the dropdown. The state is kept as classes
 * on <html>, because the header and the Earn Rewards button (assets/header-b.css)
 * react to it too:
 *   nav-search-open         the search is open.
 *   nav-search-focused      the input has had focus; from 64rem this shows the dropdown.
 *   nav-search-has-results  the dropdown shows result rows instead of the suggestions.
 *
 * WHAT A SEARCH IS
 * The dropdown runs the same search as the search page: it asks Shopify for the search
 * page URL of the typed text, rendered as one small section (Section Rendering API).
 * So the dropdown shows the first 4 products of the search page, in the same order,
 * and "See all results" leads to exactly that page.
 *
 * A suggestion label is an exact tag search. Clicking a suggestion, or typing its label
 * exactly (capitals, spaces and hyphens aside), searches tag:"Label" instead of the
 * text. The bar keeps showing the label.
 *
 * KEYBOARD
 * The input is a combobox and the dropdown its listbox. Arrow keys move through the
 * visible options, Enter opens the active one, Escape closes the search and puts the
 * focus back on the icon that opened it.
 *
 * Every listener is added with one AbortController signal, so all of them are removed
 * when the element leaves the page (for example when the theme editor re-renders the
 * header) and are not added twice when it comes back.
 */
(function () {
  if (customElements.get('nav-search')) return;

  const html = document.documentElement;

  // How long typing has to pause before a search is sent. Without a pause, typing
  // "grape" would send five requests, one per letter.
  const SEARCH_DELAY_MS = 150;

  // The typed text and the results stay in place while the bar animates shut, and are
  // cleared afterwards. Keep this longer than the close animation in nav-search.css
  // (from 64rem: --ns-reveal + --ns-grow; below it: --ns-mobile-panel).
  const CLOSE_CLEANUP_MS = 1500;

  const RESULTS_SECTION = 'nav-search-results';

  // "Beginner-Friendly", "beginner friendly" and " BEGINNER  friendly " compare equal.
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  let optionCount = 0;

  class NavSearch extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.nav-search__trigger');
      this.form = this.querySelector('.nav-search__bar');
      this.input = this.querySelector('.nav-search__input');
      this.magnifierButton = this.querySelector('.nav-search__magnifier');
      this.clearButton = this.querySelector('.nav-search__clear');
      this.backdrop = this.querySelector('.nav-search__backdrop');
      this.suggestions = this.querySelector('.nav-search__suggestions');
      this.results = this.querySelector('.nav-search__results');
      this.status = this.querySelector('.nav-search__status');
      this.suggestionMode = this.dataset.suggestionMode || 'search';
      this.labels = [...this.querySelectorAll('.nav-search__suggestion-link')]
        .map((link) => link.textContent.trim())
        .filter(Boolean);
      this.searchRequestId = 0;
      this.activeOption = null;

      this.listeners = new AbortController();
      this.addListeners(this.listeners.signal);
    }

    disconnectedCallback() {
      this.listeners.abort();
      clearTimeout(this.debounce);
      clearTimeout(this.closeCleanup);
      cancelAnimationFrame(this.panelTopFrame);
      this.panelTopFrame = null;
    }

    addListeners(signal) {
      const isOpen = () => html.classList.contains('nav-search-open');

      this.trigger?.addEventListener('click', (e) => {
        e.stopPropagation();
        isOpen() ? this.close() : this.open();
      }, { signal });

      this.backdrop?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close({ returnFocus: true });
      }, { signal });

      this.magnifierButton?.addEventListener('click', (e) => {
        e.stopPropagation();
        isOpen() ? this.input?.focus() : this.open();
      }, { signal });

      this.clearButton?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.resetSearch();
      }, { signal });

      this.form?.addEventListener('submit', (e) => this.onSubmit(e), { signal });

      this.input?.addEventListener('focus', () => {
        html.classList.add('nav-search-focused');
      }, { signal });

      this.input?.addEventListener('input', () => this.onInput(), { signal });
      this.input?.addEventListener('keydown', (e) => this.onInputKeydown(e), { signal });

      this.suggestions?.addEventListener('click', (e) => this.onSuggestionClick(e), { signal });

      document.addEventListener('click', (e) => {
        if (isOpen() && !this.contains(e.target)) this.close();
      }, { signal });

      // Escape anywhere, for example while the Clear button has focus. Escape in the
      // input is handled in onInputKeydown, which closes first, so this does nothing then.
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isOpen()) this.close({ returnFocus: true });
      }, { signal });

      window.addEventListener('scroll', () => this.queuePanelTopSync(), { passive: true, signal });
      window.addEventListener('resize', () => this.queuePanelTopSync(), { signal });
    }

    // --- Open and close ------------------------------------------------------------

    // Below 64rem the panel is fixed under the header. --ns-panel-top is the header's
    // bottom edge, kept up to date while the page scrolls.
    syncPanelTop() {
      const header = this.closest('.global-header-b, .global-header') ||
        document.querySelector('.global-header-b, .global-header');
      if (header) {
        html.style.setProperty('--ns-panel-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
      }
    }

    queuePanelTopSync() {
      if (!html.classList.contains('nav-search-open') || this.panelTopFrame) return;
      this.panelTopFrame = requestAnimationFrame(() => {
        this.panelTopFrame = null;
        this.syncPanelTop();
      });
    }

    clearSearchContent() {
      clearTimeout(this.closeCleanup);
      clearTimeout(this.debounce);
      this.searchRequestId++;
      if (this.input) this.input.value = '';
      this.clearResults();
    }

    // From 64rem the icon inside the bar opens the search. While the search is open it
    // only leads to the input, so it leaves the Tab order and its label says so.
    setOpenState(isOpen) {
      this.trigger?.setAttribute('aria-expanded', String(isOpen));
      this.input?.setAttribute('aria-expanded', String(isOpen));
      if (!this.magnifierButton) return;
      this.magnifierButton.setAttribute('aria-label', isOpen ? 'Go to search field' : 'Open search');
      this.magnifierButton.tabIndex = isOpen ? -1 : 0;
    }

    open() {
      this.clearSearchContent();
      this.syncPanelTop();
      requestAnimationFrame(() => this.syncPanelTop());
      html.classList.add('nav-search-open');
      this.setOpenState(true);
      // preventScroll: below 64rem the panel is still sliding down at this point, and
      // the input is above the header's bottom edge. Nothing should scroll to reach it.
      setTimeout(() => this.input?.focus({ preventScroll: true }), 50);
    }

    // returnFocus: for Escape and the dim. The focus goes back to the icon that opened
    // the search, so keyboard users keep their place. A click elsewhere on the page
    // leaves the focus where that click put it.
    close({ returnFocus = false } = {}) {
      const hadFocus = this.contains(document.activeElement);
      this.searchRequestId++;
      clearTimeout(this.debounce);
      if (this.panelTopFrame) {
        cancelAnimationFrame(this.panelTopFrame);
        this.panelTopFrame = null;
      }
      this.setActive(null);
      html.classList.remove('nav-search-open', 'nav-search-focused');
      this.setOpenState(false);
      clearTimeout(this.closeCleanup);
      this.closeCleanup = setTimeout(() => this.clearSearchContent(), CLOSE_CLEANUP_MS);
      if (returnFocus && hadFocus) this.focusOpener();
    }

    // The opener is the mobile icon below 64rem and the icon in the bar from 64rem.
    // Only one of the two is displayed, and only that one has a box on the page.
    focusOpener() {
      const opener = [this.trigger, this.magnifierButton].find((el) => el && el.getClientRects().length);
      opener?.focus({ preventScroll: true });
    }

    // Clear button: empties the input and brings the suggestions back.
    resetSearch() {
      clearTimeout(this.debounce);
      this.searchRequestId++;
      html.classList.add('nav-search-focused');
      if (this.input) this.input.value = '';
      this.clearResults();
      this.input?.focus();
    }

    // --- What gets searched ----------------------------------------------------------

    // A suggestion label typed exactly becomes an exact tag search. Shopify matches
    // tag:"Party Smarter" to the tag Party-Smarter, so the label is used as it is.
    toQuery(text) {
      const label = this.labels.find((l) => norm(l) === norm(text));
      return label ? `tag:"${label}"` : text;
    }

    searchPageUrl(query, sectionId) {
      const url = new URL(this.form?.getAttribute('action') || '/search', window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', query);
      if (sectionId) url.searchParams.set('section_id', sectionId);
      return url.pathname + url.search;
    }

    // Enter or the Search button. Plain text submits the form as it is; a label goes to
    // the search page for its tag search.
    onSubmit(e) {
      const text = this.input?.value.trim();
      clearTimeout(this.debounce);
      if (!text) {
        e.preventDefault();
        this.resetSearch();
        return;
      }
      const query = this.toQuery(text);
      if (query === text) return;
      e.preventDefault();
      window.location.href = this.searchPageUrl(query);
    }

    // A click on a suggestion puts its label in the bar and shows its products in the
    // dropdown. With the header setting "Preview suggestion results in search" off,
    // the link opens the search page instead.
    onSuggestionClick(e) {
      const link = e.target.closest('.nav-search__suggestion-link');
      if (!link || this.suggestionMode !== 'search') return;
      e.preventDefault();
      clearTimeout(this.debounce);
      const label = link.textContent.trim();
      if (this.input) this.input.value = label;
      html.classList.add('nav-search-focused');
      this.input?.focus();
      this.fetchResults(this.toQuery(label), this.renderSkeleton());
    }

    onInput() {
      const text = this.input.value.trim();
      clearTimeout(this.debounce);
      if (!text) {
        this.searchRequestId++;
        this.clearResults();
        return;
      }
      // Grey rows on the first keystroke, so the dropdown reacts at once. They stay
      // while typing goes on and are not drawn again, which would restart their shimmer.
      // Either branch raises searchRequestId, so an answer still on its way for the
      // earlier text is ignored.
      if (this.results?.querySelector('.nav-search__result--skeleton')) this.searchRequestId++;
      else this.renderSkeleton();
      this.debounce = setTimeout(() => {
        this.fetchResults(this.toQuery(text), ++this.searchRequestId);
      }, SEARCH_DELAY_MS);
    }

    // --- Results ---------------------------------------------------------------------

    async fetchResults(query, requestId) {
      try {
        const res = await fetch(this.searchPageUrl(query, RESULTS_SECTION));
        if (!res.ok) throw new Error(`Search returned ${res.status}`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        if (requestId !== this.searchRequestId) return;
        const section = doc.querySelector('.nav-search-results');
        if (!section) throw new Error('Search results section missing');
        this.showResults(section.innerHTML, Number(section.dataset.resultsCount) || 0);
      } catch {
        if (requestId !== this.searchRequestId) return;
        this.showResults(
          '<p class="nav-search__empty" aria-hidden="true">Results could not load. Press Enter to search.</p>',
          null
        );
      }
    }

    // count null means the request failed.
    showResults(markup, count) {
      if (!this.results) return;
      this.setActive(null);
      this.results.innerHTML = markup;
      this.results.setAttribute('aria-busy', 'false');
      html.classList.add('nav-search-has-results');
      if (count === null) this.announce('Results could not load.');
      else if (count === 0) this.announce('No results.');
      else this.announce(`${count} ${count === 1 ? 'result' : 'results'}. Use the arrow keys to browse.`);
    }

    clearResults() {
      this.setActive(null);
      html.classList.remove('nav-search-has-results');
      if (this.results) this.results.innerHTML = '';
      this.announce('');
    }

    renderSkeleton() {
      const requestId = ++this.searchRequestId;
      if (!this.results) return requestId;
      this.setActive(null);
      this.results.setAttribute('aria-busy', 'true');
      html.classList.add('nav-search-has-results');
      this.results.innerHTML = Array.from({ length: 3 }, () => `
        <div class="nav-search__result nav-search__result--skeleton" aria-hidden="true">
          <div class="nav-search__result-img">
            <span class="nav-search__skeleton-block nav-search__skeleton-image"></span>
          </div>
          <div class="nav-search__result-info">
            <span class="nav-search__skeleton-block nav-search__skeleton-title"></span>
            <span class="nav-search__skeleton-block nav-search__skeleton-price"></span>
          </div>
        </div>
      `).join('');
      return requestId;
    }

    announce(text) {
      if (this.status) this.status.textContent = text;
    }

    // --- Keyboard --------------------------------------------------------------------

    onInputKeydown(e) {
      if (e.key === 'Escape') {
        // Chrome empties a type="search" field on Escape. Stop that, so the typed text
        // fades out with the bar instead of vanishing first.
        e.preventDefault();
        this.close({ returnFocus: true });
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const options = this.visibleOptions();
        if (!options.length) return;
        e.preventDefault();
        const last = options.length - 1;
        const current = options.indexOf(this.activeOption);
        let next;
        if (e.key === 'ArrowDown') next = current >= last ? 0 : current + 1;
        else next = current <= 0 ? last : current - 1;
        this.setActive(options[next]);
        return;
      }
      if (e.key === 'Enter' && this.activeOption) {
        e.preventDefault();
        this.activeOption.click();
      }
    }

    // The options on screen: the result rows once something is typed, the suggestions
    // before that. Disabled options (a Coming Soon product) are skipped.
    visibleOptions() {
      const list = html.classList.contains('nav-search-has-results') ? this.results : this.suggestions;
      return list ? [...list.querySelectorAll('[role="option"]:not([aria-disabled="true"])')] : [];
    }

    // The focus stays in the input. aria-activedescendant tells screen readers which
    // option is active, and aria-selected is what the stylesheet highlights.
    setActive(option) {
      this.activeOption?.removeAttribute('aria-selected');
      this.activeOption = option;
      if (!this.input) return;
      if (!option) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      if (!option.id) option.id = `nav-search-option-${++optionCount}`;
      option.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', option.id);
    }
  }

  customElements.define('nav-search', NavSearch);
})();
